import '../operations.css';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheck, ArrowUpRight } from 'lucide-react';
import { assessmentsApi } from '../../api/assessments';
import type { Assessment } from '../../api/assessments';
import { scenariosApi } from '../../api/scenarios';
import { Button } from '../../components/ui/Button';

const statusStyles: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  active: 'bg-green-100 text-green-800',
  completed: 'bg-indigo-100 text-indigo-800',
};

export function AssessmentList() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [skillTag, setSkillTag] = useState('');
  const [baseline, setBaseline] = useState(0);
  const [followup, setFollowup] = useState(0);
  const [windowHours, setWindowHours] = useState(72);
  const scenarios = useQuery({ queryKey: ['scenarios'], queryFn: async () => (await scenariosApi.getAll()).data });
  const create = useMutation({
    mutationFn: () => assessmentsApi.create({ name: name.trim(), skill_tag: skillTag.trim(), baseline_scenario_id: baseline, ...(followup ? { followup_scenario_id: followup } : {}), observation_window_hours: windowHours }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['assessments'] }); setShowCreate(false); setName(''); setSkillTag(''); setBaseline(0); setFollowup(0); },
  });
  const { data: assessments, isLoading, isError } = useQuery({
    queryKey: ['assessments'],
    queryFn: async () => {
      const res = await assessmentsApi.getAll();
      return res.data;
    },
  });

  return (
    <div className="operations-page space-y-6">
      <div>
        <p className="operations-eyebrow">Readiness & evidence</p>
          <h1 className="text-2xl font-bold text-gray-900">Assessments</h1>
        <p className="text-gray-500 mt-1">
          Proof-of-resilience measurement exercises. Select one to view its evidence.
        </p>
      </div>

      <div className="flex justify-end"><Button onClick={() => setShowCreate((value) => !value)}>{showCreate ? 'Cancel' : 'New assessment'}</Button></div>
      {showCreate && <form className="card space-y-3" onSubmit={(event) => { event.preventDefault(); create.mutate(); }}>
        <h2 className="text-lg font-semibold">Create an assessment</h2>
        <label className="block text-sm">Name<input className="input mt-1" value={name} onChange={(event) => setName(event.target.value)} required /></label>
        <label className="block text-sm">Skill area<input className="input mt-1" value={skillTag} onChange={(event) => setSkillTag(event.target.value)} placeholder="Reporting suspicious messages" required /></label>
        <label className="block text-sm">Approved baseline scenario<select className="input mt-1" value={baseline} onChange={(event) => setBaseline(Number(event.target.value))} required><option value={0}>Select approved scenario</option>{scenarios.data?.filter((scenario) => scenario.status === 'approved' && scenario.kind === 'threat').map((scenario) => <option key={scenario.id} value={scenario.id}>{scenario.name}</option>)}</select></label>
        <label className="block text-sm">Approved follow-up scenario (optional)<select className="input mt-1" value={followup} onChange={(event) => setFollowup(Number(event.target.value))}><option value={0}>None</option>{scenarios.data?.filter((scenario) => scenario.status === 'approved' && scenario.kind === 'threat').map((scenario) => <option key={scenario.id} value={scenario.id}>{scenario.name}</option>)}</select></label>
        <label className="block text-sm">Observation window (hours)<input className="input mt-1" type="number" min={1} max={8760} value={windowHours} onChange={(event) => setWindowHours(Number(event.target.value))} required /></label>
        {create.isError && <p role="alert" className="text-red-700">Could not create assessment. Check that the selected scenarios are still approved.</p>}
        <Button type="submit" loading={create.isPending} disabled={!name.trim() || !skillTag.trim() || baseline <= 0}>Create assessment</Button>
      </form>}

      <section aria-label="Assessment exercises">
        {isError ? (
          <p role="alert" className="text-red-700">
            Could not load assessments. Refresh the page to try again.
          </p>
        ) : isLoading ? (
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-100 rounded"></div>
            ))}
          </div>
        ) : (
          assessments?.length ? <div className="assessment-grid">{assessments.map((assessment: Assessment) => <Link className="assessment-tile" key={assessment.id} to={`/assessments/${assessment.id}/evidence`}>
            <div className="assessment-tile-top"><ClipboardCheck size={25} /><span className={`badge capitalize ${statusStyles[assessment.status] || 'badge-gray'}`}>{assessment.status}</span></div>
            <div><h2>{assessment.name}</h2><p className="mt-2">{assessment.skill_tag || 'Security awareness'} · {assessment.observation_window_hours} hour observation window</p></div>
            <footer><span>{new Date(assessment.created_at).toLocaleDateString()}</span><strong className="flex gap-2 items-center">View evidence <ArrowUpRight size={16} /></strong></footer>
          </Link>)}</div> : <div className="card py-12 text-center text-gray-500"><ClipboardCheck className="mx-auto mb-4" size={30} /><p>No assessments yet.</p><p className="text-sm mt-2">Assessment exercises measure recognition, recovery, and reporting behavior.</p></div>
        )}
      </section>
    </div>
  );
}
